import { isArray } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import { FC, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { v4 } from 'uuid';
import cs from 'classnames';
import styles from './index.module.less';
import ReactDOM from 'react-dom';
import { showMarker } from '@/common/utils/map';
import { RangeType } from '../FirstScreenRight/ts-config';
import { useYouthMapDistance } from './useYouthMapDistance';


interface RangeMarkerRef {
  circleMarker: any;
  circleActiveMarker: any;
  contentMarker: any;
  contentActiveMarker: any;
}

/**
 * @Description 商圈围栏以及默认的围栏
 */
const Rail: FC<{
  mapIns: any,
  detail: any,
  /**
   * 商圈围栏，内含：markerOf100、markerOf200、markerOf300、markerOf500、markerOf500Walking、markerOf1000、markerOf1500Living、markerOf1500、markerOf3000Takeaway、markerOf3000
   * 来自 src/common/components/business/YouthMap/AreaDetailDrawer/components/FirstScreenMap/Rail.tsx
   * 最终用在 src/common/components/business/YouthMap/AreaDetailDrawer/components/FirstScreenRight/components/Basic/index.tsx
   * 内含围栏对象：{ circleMarker, circleActiveMarker, contentMarker, contentActiveMarker }
   */
  rangeMarkerRef: { [key: string]: RangeMarkerRef },
  /** 模型配置id */
  modelConfigId,
  /** 分享时调用接口需要的数据，如果有值代表“分享时” */
  shareData?: { tenantId, userId } | null,
  rangeType: RangeType,
  ref?: any,
  nameMarkerVisible: boolean;
}> = forwardRef(({
  mapIns,
  detail,
  rangeMarkerRef,
  modelConfigId,
  shareData,
  rangeType,
  nameMarkerVisible,
}, ref) => {

  useImperativeHandle(ref, () => ({
    relocation: methods.relocation,
  }));

  /* state */
  const curPolygonMarkerRef = useRef<any>();// 当前开店区域围栏-多边形
  const curCircleMarkerRef = useRef<{ circleMarker: any, circleActiveMarker: any }>();// 当前开店区域围栏-圆形
  /** 当前聚焦的商圈围栏，用于重新定位 */
  const curMarkerRef = useRef<any>();
  const [isInit, setIsInit] = useState<boolean>(false);// 判断是否初始化绘制完成
  const nameMarkerRef: any = useRef();

  /* hooks */
  /** 距离选项，这里以 id 作为 key */
  const { distances } = useYouthMapDistance({
    modelConfigId,
    shareData: shareData ? { tenantId: shareData.tenantId, userId: shareData.userId } : null,
    // options: {
    //   // autoFetch: false,
    //   onSuccess: (options) => {
    //     methods.getYouthMapDistance(options);
    //   }
    // }
  });
  useEffect(() => {
    methods.init();
  }, [mapIns, detail, distances]);

  useEffect(() => {
    setTimeout(() => {
      // 地图下拉框选择范围的时候高亮对应围栏
      highlightSelectMarker({ rangeType: rangeType, rangeMarkerRef, mapIns });
    }, 10);
  }, [rangeType]);

  useEffect(() => {
    // 初始化完成后，高亮选中指定围栏
    if (isInit) {
      highlightSelectMarker({ rangeType, rangeMarkerRef, mapIns });
    }
  }, [isInit]);

  useEffect(() => {
    if (!nameMarkerRef.current) return;
    if (nameMarkerVisible) {
      nameMarkerRef.current?.show?.();
    } else {
      nameMarkerRef.current?.hide?.();
    }
  }, [nameMarkerVisible]);

  // useEffect(() => { // 点击了回到商圈
  //   if (!mapIns) return;
  // }, [mapIns]);

  const methods = useMethods({

    init() {
      if (!mapIns) return;
      const {
        lng,
        lat,
        borders,
        radius,
      } = detail || {};

      if (!(+lng && +lat)) return;

      methods.clearMarkers();

      if (isArray(borders) && borders.length) {
        curPolygonMarkerRef.current = methods.createPolygon({ needFitView: true });
      } else if (radius > 0) {
        curCircleMarkerRef.current = methods.createCircle({ radius: radius, needFitView: true, needContentMarker: false });
        // 展示高亮的围栏
        curCircleMarkerRef.current.circleMarker?.hide?.();
        curCircleMarkerRef.current.circleActiveMarker?.show?.();
      }
      methods.createNameMarker();

      if (Array.isArray(distances) && distances.length) {
        // 根据选项拿到键名映射表
        const youthMapDistanceOptionsMap = (Array.isArray(distances) ? distances : []).reduce((pre, cur) => {
          pre[cur.id] = cur;
          return pre;
        }, {});

        // 根据接口返回的距离配置，绘制可见围栏
        // 注意，需要和 rangeMarkerMap 对应
        const allMarkers = [
          { name: 'markerOf100', params: { radiusType: RangeType.RANGE_100, radius: 100, content: '100m' } },
          { name: 'markerOf200', params: { radiusType: RangeType.RANGE_200, radius: 200, content: '200m' } },
          { name: 'markerOf300', params: { radiusType: RangeType.RANGE_300, radius: 300, content: '300m' } },
          { name: 'markerOf500Walking', params: { radiusType: RangeType.RANGE_500_WALKING_CIRCLE, radius: 500, content: '500m(5分钟步行圈)' } },
          { name: 'markerOf500', params: { radiusType: RangeType.RANGE_500, radius: 500, content: '500m' } },
          { name: 'markerOf1000', params: { radiusType: RangeType.RANGE_1K, radius: 1000, content: '1km' } },
          { name: 'markerOf1500Living', params: { radiusType: RangeType.RANGE_1500_LIVING_CIRCLE, radius: 1500, content: '1.5km(15分钟生活圈)' } },
          { name: 'markerOf1500', params: { radiusType: RangeType.RANGE_1500, radius: 1500, content: '1.5km' } },
          { name: 'markerOf2000', params: { radiusType: RangeType.RANGE_2000, radius: 2000, content: '2km' } },
          { name: 'markerOf3000Takeaway', params: { radiusType: RangeType.RANGE_3000_TAKEAWAY_CIRCLE, radius: 3000, content: '3km(外卖覆盖)' } },
          { name: 'markerOf3000', params: { radiusType: RangeType.RANGE_3000, radius: 3000, content: '3km' } },
        ].map(item => ({ ...item, params: { ...item.params, content: youthMapDistanceOptionsMap[item.params.radiusType]?.label || item.params.content } })); // 优先展示选项的名称

        allMarkers.forEach((item) => {
          rangeMarkerRef.current[item.name] = methods.createCircle({
            ...(item.params || {}),
            // needFitView: index === allMarkers.length - 1,
            extData: {
              tenantConfig: !!youthMapDistanceOptionsMap[item.params.radiusType]?.tenantConfig,
            },
          }); // 最后一项聚焦
        });
      }

      // rangeMarkerRef.current.markerOf100 = methods.createCircle({ radius: 100, content: '100m' }); // 100m
      // rangeMarkerRef.current.markerOf200 = methods.createCircle({ radius: 200, content: '200m' }); // 200m
      // rangeMarkerRef.current.markerOf300 = methods.createCircle({ radius: 300, content: '300m' }); // 300m
      // rangeMarkerRef.current.markerOf500Walking = methods.createCircle({ radius: 500, content: '500m(5分钟步行圈)' }); // 5分钟步行圈
      // rangeMarkerRef.current.markerOf500 = methods.createCircle({ radius: 500, content: '500m' }); // 500m
      // rangeMarkerRef.current.markerOf1000 = methods.createCircle({ radius: 1000, content: '1km' }); // 1000m
      // rangeMarkerRef.current.markerOf1500Living = methods.createCircle({ radius: 1500, content: '1.5km(15分钟生活圈)' }); // 15分钟生活圈
      // rangeMarkerRef.current.markerOf1500 = methods.createCircle({ radius: 1500, content: '1.5km' }); // 1500m
      // rangeMarkerRef.current.markerOf2000 = methods.createCircle({ radius: 2000, content: '2km' }); // 2000m
      // rangeMarkerRef.current.markerOf3000Takeaway = methods.createCircle({ radius: 3000, content: '3km(外卖覆盖)' }); // 外卖覆盖
      // rangeMarkerRef.current.markerOf3000 = methods.createCircle({ radius: 3000, content: '3km' }); // 300m0

      // 绘制是异步的，不知道什么时候完成，这里给个大一点的时间
      setTimeout(() => {
        // 初始化绘制完成
        setIsInit(true);
      }, 1000);
    },

    /** 重新定位到当前商圈 */
    relocation() {
      if (mapIns && curMarkerRef.current) {
        mapIns.setFitView(curMarkerRef.current, true, [40, 40, 40, 40], 20);
      }
    },

    /** 清除所有绘制的标记 */
    clearMarkers() {
      if (curPolygonMarkerRef.current) {
        curPolygonMarkerRef.current.remove();
      }
      if (curCircleMarkerRef.current) {
        const { circleMarker, circleActiveMarker } = curCircleMarkerRef.current || {};
        circleMarker && circleMarker.remove();
        circleActiveMarker && circleActiveMarker.remove();
        // contentMarker && contentMarker.remove();
        // contentActiveMarker && contentActiveMarker.remove();
      }
      if (rangeMarkerRef.current) {
        Object.values(rangeMarkerRef.current).forEach((item: any) => {
          if (item) {
            const { circleMarker, circleActiveMarker, contentMarker, contentActiveMarker } = item || {};
            circleMarker && circleMarker.remove();
            circleActiveMarker && circleActiveMarker.remove();
            contentMarker && contentMarker.remove();
            contentActiveMarker && contentActiveMarker.remove();
          }
        });
      }
    },

    /** 绘制多边形围栏 */
    createPolygon: ({ needFitView = false }: { needFitView?: boolean }) => {
      const { borders } = detail;
      const polygonMarker = new window.AMap.Polygon({
        path: borders,
        fillColor: '#FFA31D',
        strokeOpacity: 1,
        fillOpacity: 0.12,
        strokeColor: '#FFA31D',
        strokeWeight: 2,
        strokeStyle: 'dashed',
        strokeDasharray: [2, 2],
      });

      mapIns.add(polygonMarker);
      if (needFitView) {
        curMarkerRef.current = polygonMarker;
        mapIns.setFitView(polygonMarker, true, [40, 40, 40, 40], 20);
      }
      return polygonMarker;
    },
    /**
     * 创建圆形围栏
     * @param radius 半径
     * @param needFitView 是否需要自适应地图
     * @param needContentMarker 是否需要创建文本标注
     * @param extData 额外数据
     * @returns 内含围栏对象：{ circleMarker, circleActiveMarker, contentMarker, contentActiveMarker }
     */
    createCircle: ({ radius = 500, content = '', needFitView = false, needContentMarker = true, extData = {} }) => {
      const { lng, lat } = detail;
      /** 默认的围栏 */
      const commonOptions = {
        center: [+lng, +lat],
        radius, // 半径（米）
        strokeOpacity: 1,
        strokeDasharray: [10, 10],
        fillColor: '#FFA31D',
        strokeStyle: 'solid',
        extData: extData || {},
      };
      const circleMarker = new window.AMap.Circle({
        ...commonOptions,
        strokeColor: '#4792FD',
        strokeWeight: 0.5,
        fillOpacity: 0,
      });
      /** 高亮的围栏 */
      const circleActiveMarker = new window.AMap.Circle({
        ...commonOptions,
        strokeColor: '#FFA31D',
        strokeWeight: 0.5,
        fillOpacity: 0.2,
      });

      mapIns.add(circleMarker);
      /** 添加高亮围栏到地图，但隐藏 */
      mapIns.add(circleActiveMarker);
      circleActiveMarker?.hide?.();

      /** 默认的文本标注 */
      let contentMarker: any = null;
      /** 高亮的文本标注 */
      let contentActiveMarker: any = null;
      if (needContentMarker) {
        contentMarker = methods.createDistanceMarker({ circleMarker, content });

        // 以 circleMarker 为准，因为 circleActiveMarker 隐藏了
        contentActiveMarker = methods.createDistanceMarker({ circleMarker, content, active: true });
        if (contentActiveMarker) {
          /** 添加高亮标注到地图，但隐藏 */
          contentActiveMarker?.hide?.();
        }
      }
      if (needFitView) {
        curMarkerRef.current = circleMarker;
        mapIns.setFitView(circleMarker, true, [40, 40, 40, 40], 20);
      }
      return {
        circleMarker,
        circleActiveMarker,
        contentMarker,
        contentActiveMarker,
      };
    },
    /** 创建围栏名称标注 */
    createNameMarker: () => {
      // createNameMarker会执行多次
      if (nameMarkerRef.current) return;
      const uid:any = v4();
      const { lng, lat, areaName } = detail;
      const marker = new window.AMap.Marker({
        position: new window.AMap.LngLat(+lng, +lat),
        anchor: 'top-center',
        offset: [0, -34],
        zIndex: 20
      });
      mapIns.add(marker); // 先添加到地图上
      marker.setContent(`<div id="${uid}"></div>`);
      ReactDOM.render(<div className={styles.textMarkerWrapper}>
        <div className={styles.textMarkerCon}>
          {areaName}
        </div>
      </div>, document.getElementById(uid));
      nameMarkerRef.current = marker;
    },

    /**
     * 创建距离标注
     * @description 不同距离围栏的标注 --  如果这里有修改，记得同时修改src/common/components/business/AreaDetailDrawer/components/FirstScreenRight/components/Basic.tsx里面的handleContentMarker
     */
    createDistanceMarker: ({ circleMarker, content, active = false }) => {
      if (!content || !circleMarker) return;
      // 获取最顶部的经纬度
      // ps 高德文档中的circleMarker没有getPath方法，但实际是可用的
      const topPosition = circleMarker.getPath().reduce((acc, item) => {
        if (acc.lat < item.lat) {
          acc = item;
        }
        return acc;
      });
      const marker = new window.AMap.Marker({
        position: topPosition,
        offset: [0, 10]
      });
      mapIns.add(marker);
      showMarker(marker, <div className={cs(styles.distanceHint, active ? styles.active : '')}>{content}</div>);
      return marker;
    }
  });


  return (
    <></>
  );
});

/**
 * 范围类型与标注名称的映射关系
 * @description 注意和 allMarkers 同步
 * @demo rangeMarkerRef.current[rangeMarkerMap[radiusCode]]
 */
export const rangeMarkerMap = {
  [RangeType.DESIGNATE]: null,
  [RangeType.RANGE_100]: 'markerOf100',
  [RangeType.RANGE_200]: 'markerOf200',
  [RangeType.RANGE_300]: 'markerOf300',
  [RangeType.RANGE_500_WALKING_CIRCLE]: 'markerOf500Walking',
  [RangeType.RANGE_500]: 'markerOf500',
  [RangeType.RANGE_1K]: 'markerOf1000',
  [RangeType.RANGE_1500_LIVING_CIRCLE]: 'markerOf1500Living',
  [RangeType.RANGE_1500]: 'markerOf1500',
  [RangeType.RANGE_2000]: 'markerOf2000',
  [RangeType.RANGE_3000_TAKEAWAY_CIRCLE]: 'markerOf3000Takeaway',
  [RangeType.RANGE_3000]: 'markerOf3000',
};

/** 高亮选中指定围栏，其他围栏取消选中 */
export const highlightSelectMarker = ({
  rangeType,
  rangeMarkerRef,
  mapIns,
}: {
  rangeType: RangeType;
  /**
   * 商圈围栏，内含：markerOf100、markerOf200、markerOf300、markerOf500、markerOf500Walking、markerOf1000、markerOf1500Living、markerOf1500、markerOf3000Takeaway、markerOf3000
   * 来自 src/common/components/business/YouthMap/AreaDetailDrawer/components/FirstScreenMap/Rail.tsx
   * 最终用在 src/common/components/business/YouthMap/AreaDetailDrawer/components/FirstScreenRight/components/Basic/index.tsx
   * 内含围栏对象：{ circleMarker, circleActiveMarker, contentMarker, contentActiveMarker }
   */
  rangeMarkerRef: { [key: string]: RangeMarkerRef },
  mapIns: any;
}) => {
  // 参数验证
  if (!rangeMarkerRef || !mapIns) {
    console.warn('highlightSelectMarker: 缺少必要参数');
    return;
  }
  console.log('rangeMarkerRef.current', rangeMarkerRef.current);
  // 设置所有围栏默认样式
  Object.values(rangeMarkerMap).forEach(item => {
    const rangeMarker = item ? rangeMarkerRef.current[item] : null;
    if (rangeMarker && Object.keys(rangeMarker).length > 0) {
      const { circleMarker, circleActiveMarker, contentMarker, contentActiveMarker } = rangeMarker;
      const extData = circleMarker.getExtData?.();
      // 默认展示“tenantConfig=true”的，其他隐藏
      if (extData?.tenantConfig) {
        circleMarker.show();
        contentMarker.show();
        circleActiveMarker.hide();
        contentActiveMarker.hide();
      } else {
        circleMarker.hide();
        contentMarker.hide();
        circleActiveMarker.hide();
        contentActiveMarker.hide();
      }
    }
  });

  // 设置围栏高亮样式
  const refName = rangeMarkerMap[rangeType];
  const curRangeMarker = refName ? rangeMarkerRef.current[refName] : null;
  if (curRangeMarker && Object.keys(curRangeMarker).length > 0) {
    const { circleMarker, circleActiveMarker, contentMarker, contentActiveMarker } = curRangeMarker;
    circleMarker.hide();
    contentMarker.hide();
    circleActiveMarker.show();
    contentActiveMarker.show();

    // 地图自适应到当前选中的围栏
    mapIns.setFitView(circleActiveMarker, true, [40, 40, 40, 40], 20);
  }
};

export default Rail;


